import { Component, HostListener, OnDestroy } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { AuthService, LogoutRedirectService } from 'src/app/core/services';

interface ChipUser {
  label: string;
  initials: string;
}

interface AuthUserLike {
  email?: string;
  profile_attributes?: {
    first_name?: string;
    last_name?: string;
  };
}

interface AccountLink {
  url: string;
  labelKey: string;
}

/** Where the account menu stops being a dropdown and becomes a full-screen sheet. */
export const ACCOUNT_SHEET_QUERY = '(max-width: 640px)';

/**
 * Signed-in identity chip (reskin unit D): first name + initials avatar,
 * opening the account menu.
 *
 * FE-196 — unit D shipped this as a plain link to /user/policies, on the
 * assumption that "the account MENU stays where it lives today (header-menu /
 * profile-menu via MenusService)". That assumption was wrong about ONE item.
 * Log out never lived in either of those: `MenusService.accountMenuItems` (the
 * `/user/**` sidebar) has no logout entry, and the burger drawer's
 * `<app-login-logout>` is `display: none` above 579px. The only signed-in Log
 * out in the shell was the header's own `<app-login-logout>`, which is exactly
 * what this chip replaced — so above 579px there was no way to log out at all.
 *
 * TWO PRESENTATIONS, ONE ITEM LIST (`accountLinks` + Log out):
 *   - above 640px, a `mat-menu` dropdown anchored to the chip;
 *   - at or below it, a full-screen sheet — a dropdown pinned to a 28px avatar
 *     is a poor phone target, and the design draws the phone account menu as a
 *     sheet (`design-reference/Auth and Portal Reskin.dc.html`, screen 06).
 *
 * The sheet is this component's own markup, deliberately NOT the burger
 * drawer and NOT a restyled CDK panel. The drawer carries the whole site nav,
 * which is not what this control is for; and forcing a `mat-menu` panel
 * full-screen means overriding the inline `top`/`left` the CDK writes onto
 * `.cdk-overlay-pane`, which needs `!important` and still leaves the panel's
 * own max-height and width rules to unpick. A plain fixed-position element is
 * less code and behaves predictably.
 */
@Component({
    selector: 'app-account-chip',
    templateUrl: './account-chip.component.html',
    styleUrls: ['./account-chip.component.scss'],
    standalone: false
})
export class AccountChipComponent implements OnDestroy {
  chipUser$ = this.authService.user.pipe(
    map((user: AuthUserLike | null): ChipUser | null => {
      if (!user) { return null; }
      const first: string = user.profile_attributes?.first_name || '';
      const last: string = user.profile_attributes?.last_name || '';
      const label = first || user.email || '';
      const initials = ((first.charAt(0) || '') + (last.charAt(0) || '')).toUpperCase()
        || (user.email || '?').charAt(0).toUpperCase();
      return label ? { label, initials } : null;
    }),
  );

  /**
   * The navigation half of the menu, rendered by BOTH presentations so the two
   * cannot drift. Log out is not in here: it is an action, not a route, and it
   * is styled apart in both.
   *
   * FE — /user/profile was dropped here on the assumption the page was
   * "broken" (commit 020505cf), but the route, resolver and component all
   * compile and function (`ng build` is clean, the form loads and saves).
   * What was actually stale is the visual: the page still uses pre-reskin
   * Material form styling. Restored the link since hiding navigation to a
   * working page is worse than a page that hasn't been reskinned yet — that
   * visual pass is separate follow-up work, not a routing decision.
   */
  readonly accountLinks: AccountLink[] = [
    { url: '/user/profile', labelKey: 'menu.my_profile' },
    { url: '/user/policies', labelKey: 'menu.policies' },
  ];

  /** Drives which presentation the chip triggers. */
  readonly isPhone$ = this.breakpointObserver
    .observe(ACCOUNT_SHEET_QUERY)
    .pipe(map(state => state.matches));

  sheetOpen = false;

  private readonly subscription = new Subscription();

  constructor(
    private authService: AuthService,
    private logoutRedirectService: LogoutRedirectService,
    private router: Router,
    private breakpointObserver: BreakpointObserver,
  ) {
    // A route change while the sheet is up would leave it covering the page it
    // navigated to. Every in-sheet link closes it explicitly too; this catches
    // back/forward and anything else that navigates underneath it.
    this.subscription.add(
      this.router.events
        .pipe(filter(event => event instanceof NavigationEnd))
        .subscribe(() => this.closeSheet()),
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  /**
   * On a phone, open the sheet. On wider screens this does nothing and
   * MatMenuTrigger's own click handler opens the dropdown — the template binds
   * `[matMenuTriggerFor]` to null below 640px, and `_openMenu` returns early on
   * a null panel, so the two never both fire.
   */
  onChipClick(): void {
    if (this.breakpointObserver.isMatched(ACCOUNT_SHEET_QUERY)) {
      this.sheetOpen = true;
    }
  }

  closeSheet(): void {
    this.sheetOpen = false;
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.closeSheet();
  }

  /**
   * Same call the other two logout affordances make. `logoutToNewPlatform`
   * reads `router.url` itself for the prepopulate hand-off; the argument is
   * passed for parity with header-menu and login-logout, which both do.
   */
  logout(event: Event): void {
    event.preventDefault();
    this.closeSheet();
    this.logoutRedirectService.logoutToNewPlatform(this.router.url);
  }
}
